import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { fromPaymentMethod } from '../../../store';
import { PaymentMethodType } from '../../../models';
import AwsMarketplaceLicenseDialog from '../component/AwsMarketplaceLicenseDialog';

export interface UseAddLicenseHandlerResult {
  handleAddLicense: (onNavigate: () => void) => void;
  AwsMarketplaceDialogComponent: React.FC;
}

/**
 * Custom hook that handles the business logic for adding licenses based on payment method.
 *
 * Responsibilities:
 * - Determines the appropriate action when user wants to add licenses
 * - Manages AWS Marketplace dialog state and component
 * - Encapsulates payment method validation logic
 * - Provides a complete dialog component for easy integration
 *
 * @returns Object containing add license handler and dialog component
 */
export const useAddLicenseHandler = (): UseAddLicenseHandlerResult => {
  const [showAwsMarketplaceDialog, setShowAwsMarketplaceDialog] = useState(false);
  const currentDefaultPaymentMethod = useSelector(fromPaymentMethod.selectDefaultPaymentMethod);

  const handleAddLicense = (onNavigate: () => void) => {
    // Business rule: If payment method is AWS Marketplace, show dialog instead of navigating
    if (currentDefaultPaymentMethod === PaymentMethodType.AWS_MARKETPLACE) {
      setShowAwsMarketplaceDialog(true);
      return;
    }

    // Default behavior: navigate to plan page
    onNavigate();
  };

  const handleCloseAwsMarketplaceDialog = () => {
    setShowAwsMarketplaceDialog(false);
  };

  // Return a component that encapsulates the entire dialog logic and JSX
  const AwsMarketplaceDialogComponent: React.FC = () => (
    <AwsMarketplaceLicenseDialog
      open={showAwsMarketplaceDialog}
      onClose={handleCloseAwsMarketplaceDialog}
    />
  );

  return {
    handleAddLicense,
    AwsMarketplaceDialogComponent,
  };
};
